/**
 * Pill-tab segmented control for small, pre-canned filter buckets
 * (e.g. ALL / PUBLISHED / DRAFT / ARCHIVED on a list screen).
 *
 * Designed to slot into PageHeader's `rightSlot` so the active filter sits
 * inline with the page title — saves the vertical row a dropdown would take.
 *
 * Visual lifted from the "premium CRM console" reference design (the Ledger),
 * mapped to our design tokens.
 */

interface TabFilterProps<T extends string> {
  options: ReadonlyArray<{ value: T; label: string }>;
  current: T;
  onChange: (value: T) => void;
  /** Accessible label for the segmented control. */
  ariaLabel?: string;
}

export function TabFilter<T extends string>({
  options,
  current,
  onChange,
  ariaLabel = 'Filter',
}: TabFilterProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex items-center gap-1 rounded-xl bg-surface-muted p-1 shadow-inner"
    >
      {options.map((opt) => {
        const isActive = opt.value === current;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(opt.value)}
            className={[
              'rounded-lg px-4 py-1.5 text-[11px] uppercase tracking-wider transition-colors',
              isActive
                ? 'bg-surface font-bold text-foreground shadow-sm'
                : 'font-semibold text-foreground-muted hover:text-foreground',
            ].join(' ')}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
